// Surfaced when 3+ of the 6 services are unreachable. Built on shadcn <Alert>.
// In prod, SERVICE_URLS[*] is "" (same-origin) — guard URL construction so an empty
// value never crashes the render (was a P14 regression).

import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle } from "lucide-react";
import { SERVICE_URLS, type HealthState, type ServiceName } from "../../hooks/useServiceHealth";

interface Props {
  state: Record<ServiceName, HealthState>;
}

export function ServicesDownBanner({ state }: Props) {
  const downServices = (Object.entries(state) as [ServiceName, HealthState][])
    .filter(([, s]) => s === "down")
    .map(([name]) => name);
  const checking = Object.values(state).every((s) => s === "checking");

  if (checking || downServices.length < 3) return null;

  const isHttps = typeof window !== "undefined" && window.location.protocol === "https:";

  return (
    <Alert variant="destructive">
      <AlertTriangle className="size-4" />
      <AlertTitle>{downServices.length} of 6 services unreachable</AlertTitle>
      <AlertDescription>
        <div className="space-y-2">
          <div>
            Page loaded over <code className="font-mono">{isHttps ? "https://" : "http://"}</code> —
            either the backend stack isn't running, or this UI build was given the wrong service URLs.
          </div>
          <div className="flex gap-1.5 flex-wrap">
            {downServices.map((name) => {
              const url = SERVICE_URLS[name];
              let port = "";
              if (url) {
                try {
                  port = new URL(url).port;
                } catch {
                  port = "";
                }
              }
              return (
                <Badge key={name} variant="outline" className="font-mono">
                  {name}
                  {port && ` :${port}`}
                </Badge>
              );
            })}
          </div>
        </div>
      </AlertDescription>
    </Alert>
  );
}
